import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencil, HiTrash } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import "./FaqIndex.scss";

interface FaqItem {
  id: number;
  categoryId: number;
  categoryName?: string;
  question: string;
  answer: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const FaqIndex: React.FC = () => {
  const { t, i18n } = useTranslation(["faq", "common"]);
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const getLocalizedText = (value?: string | null) => {
    if (!value) {
      return "";
    }

    try {
      if (value && value.startsWith("{")) {
        const parsed = JSON.parse(value);
        return isRTL ? parsed.ar || parsed.en || value : parsed.en || parsed.ar || value;
      }
    } catch {
      return value;
    }

    return value;
  };

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchFaqs = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/faq?${query}`, "GET");

      if (!response.success) {
        showErrorToast(response.error || t("faq:messages.fetchError"));
        return;
      }

      const items = response.data?.items || [];
      setFaqs(items.sort((a: FaqItem, b: FaqItem) => a.displayOrder - b.displayOrder));
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      showErrorToast(t("faq:messages.fetchError"));
      console.error("Error fetching FAQ:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);

  const deleteFaq = async (id: number) => {
    if (!window.confirm(t("faq:messages.deleteConfirm"))) {
      return;
    }

    try {
      const response = await adminApi.call(`/faq/${id}`, "DELETE");

      if (!response.success) {
        showErrorToast(response.error || t("faq:messages.deleteError"));
        return;
      }

      showSuccessToast(t("faq:messages.deleteSuccess"));
      fetchFaqs(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t("faq:messages.deleteError"));
      console.error("Error deleting FAQ:", error);
    }
  };

  const columns: Column[] = [
    {
      key: "displayOrder",
      title: t("faq:table.displayOrder"),
      sortable: true,
      width: "110px",
      align: "center"
    },
    {
      key: "categoryName",
      title: t("faq:table.category"),
      sortable: false,
      render: (_, faq) => getLocalizedText(faq.categoryName) || "-"
    },
    {
      key: "question",
      title: t("faq:table.question"),
      sortable: false,
      filterable: true,
      filterKey: "question",
      render: (value) => getLocalizedText(value)
    },
    {
      key: "answer",
      title: t("faq:table.answer"),
      sortable: false,
      render: (value) => {
        const text = getLocalizedText(value);
        return text.length > 140 ? `${text.substring(0, 140)}...` : text;
      }
    },
    {
      key: "isActive",
      title: t("faq:table.status"),
      sortable: true,
      width: "120px",
      align: "center",
      render: (value) => (
        <span className={`badge ${value ? "bg-success" : "bg-danger"}`}>
          {value ? t("faq:status.active") : t("faq:status.inactive")}
        </span>
      )
    },
    {
      key: "actions",
      title: t("faq:table.actions"),
      sortable: false,
      width: "150px",
      align: "center",
      render: (_, faq) => (
        <div className="action-buttons">
          {hasPermission("Update FAQ") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => navigate(`/faqs/edit/${faq.id}`)}
              tooltip={t("faq:editFaq")}
            />
          )}
          {hasPermission("Delete FAQ") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteFaq(faq.id)}
              tooltip={t("faq:deleteFaq")}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <PageLayout
      title={t("faq:title")}
      subtitle={t("faq:subtitle")}
      breadcrumbs={[{ label: t("faq:breadcrumbs.faq") }]}
      className={isRTL ? "rtl" : "ltr"}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={faqs}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={false}
        refreshable={true}
        addButton={hasPermission("Create FAQ") ? {
          label: t("faq:addNew"),
          onClick: () => navigate("/faqs/create")
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters((prev) => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchFaqs(currentPage, pageSize)}
        emptyMessage={t("faq:messages.noFaq")}
      />
    </PageLayout>
  );
};
